import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { DOC_TYPES } from "@/lib/constants";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { SignatureStep } from "@/components/wizard/Steps";
import { FinishStep } from "@/components/wizard/ReviewFinish";

export default function CollectSignatures() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [client, setClient] = useState(null);
  const [company, setCompany] = useState(null);
  const [techName, setTechName] = useState("");
  const [sigs, setSigs] = useState({ client: null, tech: null });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    Promise.all([api.get(`/documents/${id}`), api.get("/company")]).then(async ([d, c]) => {
      setDoc(d.data); setCompany(c.data); setTechName(d.data.technician || c.data.responsible || "");
      if (d.data.client_id) { const cl = await api.get(`/clients/${d.data.client_id}`); setClient(cl.data.client); }
    }).catch(() => { toast.error("Documento não encontrado"); navigate("/documents"); });
  }, [id]);

  const save = async () => {
    if (!sigs.client && !sigs.tech) { toast.error("Desenhe ao menos uma assinatura"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/documents/${id}/signatures`, { client_name: client?.name || "", client_signature: sigs.client || "", technician_name: techName, technician_signature: sigs.tech || "" });
      setDone(data); toast.success("Assinaturas salvas!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); } finally { setBusy(false); }
  };

  if (!doc) return <div className="h-64 flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  const label = DOC_TYPES[doc.type]?.label;

  return (
    <div className="max-w-2xl mx-auto space-y-4 pb-24 md:pb-8" data-testid="collect-signatures">
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => navigate(`/documents/${id}`)} className="flex items-center gap-1 text-muted-foreground text-sm h-10" data-testid="collect-back"><ArrowLeft className="h-4 w-4" /> Voltar</button>
        <p className="font-display font-bold">{label} {doc.number}</p><span className="w-16" />
      </div>
      <Card className="p-4 sm:p-6">
        {done ? <FinishStep doc={done} client={client} type={doc.type} /> : (
          <>
            <h2 className="font-display font-extrabold text-xl mb-1">Coletar assinaturas</h2>
            <p className="text-sm text-muted-foreground mb-4">{client?.name} • {doc.date}{doc.signature?.data ? " • cliente já assinou" : ""}{doc.technician_signature?.data ? " • técnico já assinou" : ""}</p>
            <SignatureStep clientName={client?.name || "Cliente"} techName={techName} onTechName={setTechName} sigs={sigs} setSigs={setSigs} />
          </>
        )}
      </Card>
      {!done && (
        <div className="fixed bottom-20 left-0 right-0 md:static z-40 bg-background/95 backdrop-blur border-t md:border-0 border-border p-3 md:p-0">
          <div className="max-w-2xl mx-auto"><Button className="w-full h-14 text-base" onClick={save} disabled={busy} data-testid="collect-save">{busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Check className="h-5 w-5 mr-1" /> Salvar assinaturas</>}</Button></div>
        </div>
      )}
    </div>
  );
}
